/**
 * CycleCompleteView
 *
 * Shown when the user's cycle is complete. Displays:
 * - Results summary with personalized interpretation
 * - Cryo storage status
 * - "What's next" decision cards
 * - Annual reminders
 */

import React from "react";
import { View, Text, Pressable } from "react-native";
import {
  CheckCircle2,
  Snowflake,
  TrendingUp,
  Heart,
  Users,
  Calendar,
  ChevronRight,
  FileText,
  Plus,
  type LucideIcon,
} from "lucide-react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import type { PlanAction } from "../plan.types";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
// ─── Types ───

export type CycleResult = {
  cycleType: string;
  eggsRetrieved?: number;
  matureEggs?: number;
  fertilized?: number;
  blastocysts?: number;
  frozen?: number;
  transferred?: boolean;
  pregnancyResult?: "positive" | "negative" | "pending" | null;
  interpretation?: string;
};

export type CryoStorageInfo = {
  itemCount: number;
  itemType: "eggs" | "embryos" | "sperm";
  facilityName?: string;
  annualCost?: string;
  nextPaymentDate?: string;
};

export type WhatsNextOption = {
  id: string;
  title: string;
  description: string;
  icon: "repeat" | "baby" | "pause" | "heart" | "search";
  action?: PlanAction;
};

type Props = {
  journeyTitle: string;
  result?: CycleResult;
  cryoStorage?: CryoStorageInfo;
  whatsNextOptions: WhatsNextOption[];
  userAge?: number;
  onAction?: (action: PlanAction) => void;
};

// ─── Sub-components ───

function ResultsCard({
  result,
  userAge,
}: {
  result: CycleResult;
  userAge?: number;
}) {
  const t = useTranslations();
  const maturityRate =
    result.eggsRetrieved && result.matureEggs
      ? Math.round((result.matureEggs / result.eggsRetrieved) * 100)
      : null;

  return (
    <View
      style={{
        backgroundColor: "rgba(78, 115, 66, 0.06)",
        borderRadius: 20,
        padding: 20,
        borderWidth: 1,
        borderColor: "rgba(78, 115, 66, 0.15)",
        gap: 16,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <CheckCircle2 size={20} color={EMBER.colors.green} />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            color: EMBER.colors.green,
            fontWeight: "600",
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          {t("homepage.cycle_complete.your_results")}
        </Text>
      </View>

      {/* Pregnancy result (IVF) */}
      {result.pregnancyResult === "positive" ? (
        <View
          style={{
            backgroundColor: "rgba(78, 115, 66, 0.1)",
            borderRadius: 12,
            padding: 16,
            alignItems: "center",
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 20,
              color: EMBER.colors.green,
              fontWeight: "700",
            }}
          >
            {t("homepage.cycle_complete.positive")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textMid,
              fontWeight: "400",
              marginTop: 4,
              textAlign: "center",
            }}
          >
            {t("homepage.cycle_complete.positive_message")}
          </Text>
        </View>
      ) : result.pregnancyResult === "negative" ? (
        <View
          style={{
            backgroundColor: "rgba(168, 86, 58, 0.06)",
            borderRadius: 12,
            padding: 16,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              lineHeight: 21,
              color: EMBER.colors.text,
              fontWeight: "400",
            }}
          >
            {t("homepage.cycle_complete.negative_message")}
          </Text>
        </View>
      ) : null}

      {/* Egg/embryo stats */}
      {[
        result.eggsRetrieved,
        result.matureEggs,
        result.fertilized,
        result.blastocysts,
        result.frozen,
      ].some((value) => value !== undefined) ? (
        <View style={{ gap: 8 }}>
          {result.eggsRetrieved !== undefined ? (
            <StatRow
              label={t("homepage.shared.eggs_retrieved")}
              value={result.eggsRetrieved}
            />
          ) : null}
          {result.matureEggs !== undefined ? (
            <StatRow
              label={t("homepage.shared.mature_eggs")}
              value={result.matureEggs}
              detail={
                maturityRate
                  ? t("homepage.cycle_complete.maturity_rate", {
                      params: { maturityRate: String(maturityRate) },
                    })
                  : undefined
              }
            />
          ) : null}
          {result.fertilized !== undefined ? (
            <StatRow
              label={t("homepage.cycle_complete.fertilized")}
              value={result.fertilized}
            />
          ) : null}
          {result.blastocysts !== undefined ? (
            <StatRow
              label={t("homepage.shared.blastocysts")}
              value={result.blastocysts}
            />
          ) : null}
          {result.frozen !== undefined ? (
            <StatRow
              label={t("homepage.cycle_complete.frozen")}
              value={result.frozen}
            />
          ) : null}
        </View>
      ) : null}

      {/* Personalized interpretation — inline, no chrome stripe */}
      {result.interpretation ? (
        <View style={{ marginTop: 4 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              color: EMBER.colors.warm,
              fontWeight: "600",
              letterSpacing: 0.3,
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            {t("homepage.cycle_complete.what_numbers_mean")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              lineHeight: 19,
              color: EMBER.colors.text,
              fontWeight: "400",
            }}
          >
            {result.interpretation}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

function StatRow({
  label,
  value,
  detail,
}: {
  label: string;
  value: number;
  detail?: string;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          color: EMBER.colors.textMid,
          fontWeight: "400",
        }}
      >
        {label}
      </Text>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 18,
            color: EMBER.colors.text,
            fontWeight: "700",
          }}
        >
          {value}
        </Text>
        {detail ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              color: EMBER.colors.textLight,
              fontWeight: "400",
            }}
          >
            ({detail})
          </Text>
        ) : null}
      </View>
    </View>
  );
}

function CryoCard({
  info,
  onAction,
}: {
  info: CryoStorageInfo;
  onAction?: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("CryoCard");
  const typeLabel =
    info.itemType === "eggs"
      ? t("homepage.cycle_complete.eggs")
      : info.itemType === "embryos"
        ? t("homepage.cycle_complete.embryos")
        : t("homepage.cycle_complete.samples");

  return (
    <View
      style={{
        backgroundColor: "rgba(107, 90, 78, 0.06)",
        borderRadius: 16,
        padding: 16,
        borderWidth: 1,
        borderColor: "rgba(107, 90, 78, 0.12)",
        gap: 8,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <Snowflake size={18} color={EMBER.colors.blue} />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            color: EMBER.colors.textLight,
            fontWeight: "600",
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          {t("homepage.cycle_complete.cryo_storage")}
        </Text>
      </View>

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 16,
          color: EMBER.colors.text,
          fontWeight: "700",
        }}
      >
        {info.facilityName
          ? t("homepage.cycle_complete.count_type_at_facility", {
              params: {
                itemCount: String(info.itemCount),
                typeLabel,
                facilityName: info.facilityName,
              },
            })
          : t("homepage.cycle_complete.count_type", {
              params: {
                itemCount: String(info.itemCount),
                typeLabel,
              },
            })}
      </Text>

      {info.annualCost ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: EMBER.colors.textMid,
            fontWeight: "400",
          }}
        >
          {info.nextPaymentDate
            ? t("homepage.cycle_complete.storage_with_payment", {
                params: {
                  annualCost: info.annualCost,
                  nextPaymentDate: info.nextPaymentDate,
                },
              })
            : t("homepage.cycle_complete.storage_only", {
                params: { annualCost: info.annualCost },
              })}
        </Text>
      ) : null}

      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(t("homepage.cycle_complete.view_cryo_vault_details")),
            "homepage.cycle_complete.view_cryo_vault_details",
          );
          onAction?.({
            label: t("homepage.cycle_complete.view_cryo_vault"),
            screen: "CryoVaultScreen",
          });
        }}
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 4,
          marginTop: 4,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: EMBER.colors.warm,
            fontWeight: "600",
          }}
        >
          {t("homepage.cycle_complete.view_cryo_vault_details")}
        </Text>
        <ChevronRight size={14} color={EMBER.colors.warm} />
      </Pressable>
    </View>
  );
}

function EggFreezingAfterRetrievalCard({
  onAction,
}: {
  onAction?: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("EggFreezingAfterRetrievalCard");
  const rows = [
    {
      label: t("homepage.cycle_complete.mature_eggs_matter"),
      body: t("homepage.cycle_complete.mature_eggs_matter_body"),
    },
    {
      label: t("homepage.cycle_complete.storage_ongoing"),
      body: t("homepage.cycle_complete.storage_ongoing_body"),
    },
    {
      label: t("homepage.cycle_complete.decide_later"),
      body: t("homepage.cycle_complete.decide_later_body"),
    },
  ];

  const actions: {
    title: string;
    subtitle: string;
    icon: LucideIcon;
    action: PlanAction;
  }[] = [
    {
      title: t("homepage.cycle_complete.open_cryo_vault"),
      subtitle: t("homepage.cycle_complete.open_cryo_vault_subtitle"),
      icon: Snowflake,
      action: {
        label: t("homepage.cycle_complete.view_cryo_vault"),
        screen: "CryoVaultScreen",
      },
    },
    {
      title: t("homepage.cycle_complete.add_documents"),
      subtitle: t("homepage.cycle_complete.add_documents_subtitle"),
      icon: FileText,
      action: {
        label: t("homepage.cycle_complete.upload_documents"),
        screen: "DocumentsScreen",
      },
    },
    {
      title: t("homepage.cycle_complete.consider_another"),
      subtitle: t("homepage.cycle_complete.consider_another_subtitle"),
      icon: Plus,
      action: {
        label: t("homepage.cycle_complete.start_another"),
        screen: "CycleDetailScreen",
      },
    },
  ];

  return (
    <View
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.35)",
        borderRadius: 16,
        padding: 16,
        borderWidth: 1,
        borderColor: "rgba(255, 255, 255, 0.4)",
        gap: 14,
      }}
    >
      <View>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            color: EMBER.colors.textLight,
            fontWeight: "600",
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          {t("homepage.cycle_complete.after_retrieval")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 17,
            lineHeight: 22,
            color: EMBER.colors.text,
            fontWeight: "700",
            marginTop: 4,
          }}
        >
          {t("homepage.cycle_complete.frozen_eggs_decisions")}
        </Text>
      </View>

      <View style={{ gap: 10 }}>
        {rows.map((row) => (
          <View key={row.label} style={{ gap: 2 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                lineHeight: 17,
                color: EMBER.colors.text,
                fontWeight: "700",
              }}
            >
              {row.label}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                lineHeight: 19,
                color: EMBER.colors.textMid,
                fontWeight: "400",
              }}
            >
              {row.body}
            </Text>
          </View>
        ))}
      </View>

      <View style={{ gap: 2 }}>
        {actions.map((item, index) => {
          const Icon = item.icon;
          return (
            <View key={item.title}>
              {index > 0 ? (
                <View
                  style={{
                    height: 1,
                    backgroundColor: EMBER.colors.border,
                    opacity: 0.3,
                    marginVertical: 4,
                  }}
                />
              ) : null}
              <Pressable
                onPress={() => {
                  capturePress(pressLabel(item.title), "after_retrieval_row");
                  onAction?.(item.action);
                }}
                style={({ pressed }) => ({
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                  paddingVertical: 10,
                  opacity: pressed ? 0.72 : 1,
                })}
              >
                <View
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    backgroundColor: "rgba(184, 74, 22, 0.08)",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon size={17} color={EMBER.colors.warm} strokeWidth={1.8} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 14,
                      color: EMBER.colors.text,
                      fontWeight: "600",
                    }}
                  >
                    {item.title}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      lineHeight: 17,
                      color: EMBER.colors.textMid,
                      fontWeight: "400",
                      marginTop: 2,
                    }}
                  >
                    {item.subtitle}
                  </Text>
                </View>
                <ChevronRight size={15} color={EMBER.colors.textFaint} />
              </Pressable>
            </View>
          );
        })}
      </View>
    </View>
  );
}

const NEXT_ICONS = {
  repeat: TrendingUp,
  baby: Heart,
  pause: Calendar,
  heart: Heart,
  search: Users,
} as const;

function WhatsNextCard({
  option,
  onAction,
}: {
  option: WhatsNextOption;
  onAction?: (action: PlanAction) => void;
}) {
  const Icon = NEXT_ICONS[option.icon] ?? Heart;
  const capturePress = usePressCapture("WhatsNextCard");

  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(option.title), "whats_next_option");
        if (option.action) onAction?.(option.action);
      }}
      style={{
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 12,
        paddingVertical: 12,
        paddingHorizontal: 4,
      }}
    >
      <View
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          backgroundColor: "rgba(184, 74, 22, 0.08)",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={18} color={EMBER.colors.warm} />
      </View>
      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            color: EMBER.colors.text,
            fontWeight: "600",
          }}
        >
          {option.title}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 19,
            color: EMBER.colors.textMid,
            fontWeight: "400",
            marginTop: 2,
          }}
        >
          {option.description}
        </Text>
      </View>
      <ChevronRight
        size={16}
        color={EMBER.colors.textFaint}
        style={{ marginTop: 2 }}
      />
    </Pressable>
  );
}

// ─── Main Component ───

export default function CycleCompleteView({
  journeyTitle,
  result,
  cryoStorage,
  whatsNextOptions,
  userAge,
  onAction,
}: Props) {
  const t = useTranslations();
  const isEggFreezing = result?.cycleType === "Egg Freezing";

  return (
    <View style={{ gap: 16 }}>
      {/* Hero */}
      <View style={{ alignItems: "center", gap: 8, paddingVertical: 8 }}>
        <CheckCircle2 size={32} color={EMBER.colors.green} />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 22,
            color: EMBER.colors.text,
            fontWeight: "700",
          }}
        >
          {t("homepage.cycle_complete.cycle_is_complete")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            color: EMBER.colors.textMid,
            fontWeight: "400",
          }}
        >
          {journeyTitle}
        </Text>
      </View>

      {/* Results */}
      {result ? <ResultsCard result={result} userAge={userAge} /> : null}

      {/* Cryo storage */}
      {cryoStorage ? <CryoCard info={cryoStorage} onAction={onAction} /> : null}

      {isEggFreezing ? (
        <EggFreezingAfterRetrievalCard onAction={onAction} />
      ) : null}

      {/* What's next */}
      {whatsNextOptions.length > 0 ? (
        <View
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.35)",
            borderRadius: 16,
            padding: 16,
            borderWidth: 1,
            borderColor: "rgba(255, 255, 255, 0.4)",
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              color: EMBER.colors.textLight,
              fontWeight: "600",
              letterSpacing: 0.5,
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            {t("homepage.cycle_complete.whats_next")}
          </Text>
          {whatsNextOptions.map((option, idx) => (
            <View key={option.id}>
              {idx > 0 ? (
                <View
                  style={{
                    height: 1,
                    backgroundColor: EMBER.colors.border,
                    opacity: 0.3,
                  }}
                />
              ) : null}
              <WhatsNextCard option={option} onAction={onAction} />
            </View>
          ))}
        </View>
      ) : null}

      {/* Emotional acknowledgment */}
      <View
        style={{
          backgroundColor: "rgba(253, 218, 176, 0.12)",
          borderRadius: 12,
          padding: 14,
          borderWidth: 1,
          borderColor: "rgba(253, 218, 176, 0.25)",
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            lineHeight: 21,
            color: EMBER.colors.text,
            fontWeight: "400",
          }}
        >
          {t("homepage.cycle_complete.emotional_message")}
        </Text>
      </View>

      {/* Annual reminders */}
      <View
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.25)",
          borderRadius: 12,
          padding: 14,
          borderWidth: 1,
          borderColor: "rgba(255, 255, 255, 0.35)",
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            color: EMBER.colors.textLight,
            fontWeight: "600",
            letterSpacing: 0.5,
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          {t("homepage.cycle_complete.annual_reminders")}
        </Text>
        {[
          t("homepage.cycle_complete.storage_payment_reminder"),
          t("homepage.cycle_complete.annual_wellness_check"),
          t("homepage.cycle_complete.review_fertility_plan"),
        ].map((reminder) => (
          <View
            key={reminder}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              paddingVertical: 4,
            }}
          >
            <Calendar size={14} color={EMBER.colors.textFaint} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                color: EMBER.colors.textMid,
                fontWeight: "400",
              }}
            >
              {reminder}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}
